import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import i18n from '@/i18n';
import { del, edit, get, post } from '@/utils';
// Import DIRECT (nu prin barrel-ul '@/components') — evită ciclul hooks ↔ components.
import {
	IServerColumnConfig,
	IServerTableConfig
} from '@/components/table/column-config';
import {
	IAppUser,
	IUpdateAppUser,
	IRestrictObj,
	IUserSanction,
	IUserSanctionsResponse,
	IUserAffinities
} from './types';

// BE așteaptă { reason, durationMinutes } (0 = permanent). În UI `until` e timestamp absolut
// (0 = permanent), așa că îl convertim aici în minute față de momentul curent.
const toDurationMinutes = (until?: number): number => {
	if (!until || until <= 0) return 0;
	const mins = Math.round((until - dayjs().valueOf()) / 60000);
	return mins > 0 ? mins : 1;
};

type AppUsersResponse = {
	items: IAppUser[];
	pageNumber: number;
	totalPages: number;
	totalCount: number;
	ascending: boolean;
	hasNextPage: boolean;
	hasPreviousPage: boolean;
	orderBy: string | null;
};

const appendPaginationParams = (query: string, page: number, items: number): string => {
	const separator = query.includes('?') ? '&' : '?';
	return `${query}${separator}PageNumber=${page + 1}&PageSize=${items}`;
};

async function fetchAppUsers(
	page: number,
	items: number,
	onResponse?: (page: number, items: number) => void
): Promise<IAppUser[]> {
	try {
		const res = await get(`api/users?PageNumber=${page + 1}&PageSize=${items}`);
		const data = res.data as AppUsersResponse;
		onResponse?.(data.totalPages, data.totalCount);
		return data.items || [];
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('citizens.errorLoad'));
	}
}

async function fetchQueryUsers(
	query: string,
	page: number,
	items: number,
	onResponse?: (page: number, items: number) => void
): Promise<IAppUser[]> {
	try {
		const res = await get(`api/users${appendPaginationParams(query, page, items)}`);
		const data = res.data as AppUsersResponse;
		onResponse?.(data.totalPages, data.totalCount);
		return data.items || [];
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('citizens.errorLoad'));
	}
}

async function fetchSingleAppUser(userId: number | string): Promise<IAppUser> {
	try {
		const res = await get(`api/users/${userId}`);
		return res.data as IAppUser;
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('citizens.errorLoadUser'));
	}
}

async function deleteUser(userId: number | string): Promise<string | number> {
	try {
		const res = await del(`api/users/${userId}`);
		return res?.status;
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('citizens.errorDelete'));
	}
}

// Update prin PATCH: trimitem obiectul complet al utilizatorului (inclusiv administrationIds).
async function updateAppUser(
	userId: number | string,
	updatedUser: IUpdateAppUser
): Promise<string | number> {
	try {
		const res = await edit(`api/users/${userId}`, { ...updatedUser });
		return res?.status && res.status >= 200 && res.status < 300 ? 200 : 400;
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('citizens.errorUpdate'));
	}
}

// Schimbare de rol pe endpointurile dedicate. Ordinea e OBLIGATORIE: întâi POST-ul
// rolului nou (un user nu poate rămâne fără rol), abia apoi DELETE pe cel vechi.
// Dacă DELETE-ul vechiului rol eșuează, încercăm rollback (scoatem rolul nou) ca să
// nu lăsăm userul cu două roluri.
async function changeUserRole(
	userId: number | string,
	newRoleId: number,
	oldRoleId?: number
): Promise<void> {
	try {
		await post(`api/users/${userId}/roles/${newRoleId}`);
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('webUsers.changeRoleError'));
	}

	if (oldRoleId === undefined || oldRoleId === newRoleId) return;

	try {
		await del(`api/users/${userId}/roles/${oldRoleId}`);
	} catch (e) {
		try {
			await del(`api/users/${userId}/roles/${newRoleId}`);
		} catch {
			// rollback eșuat — userul a rămas cu ambele roluri; semnalăm eroarea inițială
		}
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('webUsers.changeRoleError'));
	}
}

async function resetUserPassword(userId: number | string): Promise<string | number> {
	try {
		const res = await post(`api/users/${userId}/reset-password`);
		return res?.status && res.status >= 200 && res.status < 300 ? 200 : 400;
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('citizens.errorResetPassword'));
	}
}

async function muteAppUser(
	userId: number | string,
	restrictObj: IRestrictObj
): Promise<string | number> {
	try {
		const res = await post(`api/users/${userId}/mute`, {
			reason: restrictObj.reason,
			durationMinutes: toDurationMinutes(restrictObj.until)
		});
		return res?.status && res.status >= 200 && res.status < 300 ? 200 : 400;
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('citizens.errorMute'));
	}
}

async function unmuteAppUser(userId: number | string): Promise<string | number> {
	try {
		const res = await post(`api/users/${userId}/unmute`);
		return res?.status && res.status >= 200 && res.status < 300 ? 200 : 400;
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('citizens.errorUnmute'));
	}
}

// PATCH api/users/:id/status → schimbă statusul utilizatorului (Activ / Blocat / Restricționat).
async function patchAppUserStatus(
	userId: number | string,
	statusId: number
): Promise<string | number> {
	try {
		const res = await edit(`api/users/${userId}/status`, { statusId });
		return res?.status && res.status >= 200 && res.status < 300 ? 200 : 400;
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('citizens.errorUpdateStatus'));
	}
}

// GET api/users/:id/sanctions → lista de sancțiuni (restricții/blocări) a utilizatorului.
async function fetchUserSanctions(userId: number | string): Promise<IUserSanction[]> {
	try {
		const res = await get(`api/users/${userId}/sanctions`);
		const data = res.data as IUserSanctionsResponse | IUserSanction[];
		return Array.isArray(data) ? data : data.items ?? [];
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('citizens.errorLoadSanctions'));
	}
}

// GET api/users/:id/affinities → semnalele de afinitate (dimensiuni + sumar) pentru tab-ul „Afinități".
async function fetchUserAffinities(userId: number | string): Promise<IUserAffinities | null> {
	try {
		const res = await get(`api/users/${userId}/affinities`);
		return (res.data as IUserAffinities) ?? null;
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('citizens.errorLoadAffinities'));
	}
}

// GET api/users/table-config — configurația coloanelor tabelelor de utilizatori
// (Utilizatori web + Cetățeni împart același endpoint). Se corelează cu definițiile
// locale în fiecare pagină prin applyServerColumnConfig.
async function fetchUsersTableConfig(): Promise<IServerColumnConfig[]> {
	try {
		const res = await get('api/users/table-config');
		const data = res.data as IServerTableConfig;
		return data?.columns ?? [];
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('citizens.errorLoadTableConfig'));
	}
}

export {
	fetchAppUsers,
	fetchSingleAppUser,
	deleteUser,
	fetchQueryUsers,
	fetchUsersTableConfig,
	updateAppUser,
	changeUserRole,
	resetUserPassword,
	muteAppUser,
	unmuteAppUser,
	patchAppUserStatus,
	fetchUserSanctions,
	fetchUserAffinities
};
